import { MoreHorizontalIcon, CloudUpload, CloudOff, Cloud } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Badge,
  Button,
  ButtonGroup,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@f2e/ui/components/base"
import { ClientChannel } from "@/types"

import { ChannelPublishState } from "./publishState"

export interface PublishMenuButtonProps {
  channel: ClientChannel
  state: ChannelPublishState
  onPublish?: () => void
  onUnpublish?: () => void
}

export const PublishMenuButton = ({
  channel,
  state,
  onPublish,
  onUnpublish,
}: PublishMenuButtonProps) => {
  const { t } = useTranslation()
  const stateContent = {
    unsaved_local: {
      badgeClassName: "border-amber-300 bg-amber-50 text-amber-700",
      primaryLabel: t("workspace.channels.publishMenu.labels.saveFirst"),
      badgeLabel: t("workspace.channels.publishMenu.badges.unsaved"),
      helperText: t("workspace.channels.publishMenu.helpers.unsavedLocal"),
      primaryIcon: "publish",
      primaryDisabled: true,
    },
    never_published: {
      badgeClassName: "border-slate-300 bg-slate-50 text-slate-700",
      primaryLabel: t("workspace.agent.publishDialog.labels.publish"),
      badgeLabel: t("workspace.channels.publishMenu.badges.notLive"),
      helperText: t("workspace.channels.publishMenu.helpers.neverPublished"),
      primaryIcon: "publish",
      primaryDisabled: false,
    },
    unpublished: {
      badgeClassName: "border-slate-300 bg-slate-50 text-slate-700",
      primaryLabel: t("workspace.agent.publishDialog.labels.publish"),
      badgeLabel: t("workspace.channels.publishMenu.badges.offline"),
      helperText: t("workspace.channels.publishMenu.helpers.unpublished"),
      primaryIcon: "publish",
      primaryDisabled: false,
    },
    pending_publish: {
      badgeClassName: "border-blue-300 bg-blue-50 text-blue-700",
      primaryLabel: t("workspace.channels.publishMenu.labels.publishChanges"),
      badgeLabel: t("workspace.channels.publishMenu.badges.changesPending"),
      helperText: t("workspace.channels.publishMenu.helpers.pendingPublish"),
      primaryIcon: "publish",
      primaryDisabled: false,
    },
    live: {
      badgeClassName: "border-green-300 bg-green-50 text-green-700",
      primaryLabel: t("workspace.channels.publishMenu.labels.published"),
      badgeLabel: t("workspace.channels.publishMenu.badges.live"),
      helperText: null,
      primaryIcon: "live",
      primaryDisabled: true,
    },
  } satisfies Record<
    ChannelPublishState,
    {
      badgeClassName: string
      primaryLabel: string
      badgeLabel: string
      helperText: string | null
      primaryIcon: "publish" | "live"
      primaryDisabled: boolean
    }
  >
  const {
    badgeClassName,
    primaryLabel,
    badgeLabel,
    helperText,
    primaryIcon,
    primaryDisabled,
  } = stateContent[state]
  const showUnpublishAction =
    channel.isPublished && (state === "pending_publish" || state === "live")

  const handleUnpublish = () => {
    if (!showUnpublishAction) return
    onUnpublish?.()
  }

  const handlePublish = () => {
    if (primaryDisabled) return
    onPublish?.()
  }

  const primaryButtonIcon =
    primaryIcon === "live" ? (
      <Cloud className="h-4 w-4 mr-1 text-foreground" />
    ) : (
      <CloudUpload className="h-4 w-4 mr-1 text-foreground" />
    )

  const primaryButton = (
    <Button
      size="sm"
      variant="outline"
      onClick={handlePublish}
      disabled={primaryDisabled}
    >
      {primaryButtonIcon}
      {primaryLabel}
    </Button>
  )

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className={badgeClassName}>
          {badgeLabel}
        </Badge>
        {showUnpublishAction ? (
          <ButtonGroup>
            {primaryButton}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  aria-label={t("workspace.channels.publishMenu.moreOptions")}
                  className="px-2 focus-visible:ring-0 focus-visible:ring-offset-0"
                >
                  <MoreHorizontalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleUnpublish}>
                  <CloudOff className="h-4 w-4 mr-3 text-foreground" />
                  {t("workspace.agent.publishDialog.labels.unpublish")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </ButtonGroup>
        ) : (
          primaryButton
        )}
      </div>
      {helperText ? (
        <p className="max-w-72 text-right text-xs text-muted-foreground">
          {helperText}
        </p>
      ) : null}
    </div>
  )
}

export default PublishMenuButton
